import React, { useState } from "react";
import {
    Box,
    Button,
    Input,
    Text,
    VStack,
    ChakraProvider,
    extendTheme,
    Flex,
    Divider,
    Menu,
    MenuButton,
    Icon,
    MenuList,
    MenuItem,
    IconButton
} from "@chakra-ui/react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    useDroppable,
    useDraggable,
    closestCorners,
    rectIntersection
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TextBlockEditor from "./TextBlockEditor/TextBlockEditor";
import TextBlockPreview from "./TextBlockEditor/TextBlockPreview";
import { LuTrash2, LuMove, LuMoreVertical, LuType, LuSpace, LuImage, LuVideo } from "react-icons/lu";
// Import the separated text block preview
import phoneImage from '../../images/phone.png';
import Header from "../Header";
import VideoBlockEditor from "./VideoEditor/VideoEditor";
import VideoBlockPreview from "./VideoEditor/VideoEditorPreview";
import ImageBlockEditor from "./ImageBlockEditor/ImageBlockEditor";
import ImageBlockPreview from "./ImageBlockEditor/ImageBlockPreview";
import ButtonBlockEditor from "./ButtonBlockEditor/ButtonBlockEditor";
import ButtonBlockPreview from "./ButtonBlockEditor/ButtonBlockPreview";
const theme = extendTheme();

const blockTypes = [{
    name: "text", icon: <LuType />
}, {
    name: "button", icon: <LuSpace />
}, {
    name: "image", icon: <LuImage />
}, {
    name: "video", icon: <LuVideo />
}];

const getDefaultProps = (type) => {
    switch (type) {
        case "text":
            return {
                text: "Sample text",
                color: "#000000",
                fontSize: "16",
                alignment: "left",
                bold: false,
                italic: false,
                underline: false,
                marginTop: "10",
                marginRight: "0",
                marginBottom: "0",
                marginLeft: "10"
            };
        case "image":

            return {
                imageUploadUrl: "https://admin.xnotify.ai/static/media/Logo.abcad9b0257193e76d8d.png",
                imageUploadUrlLink: "https://admin.xnotify.ai/static/media/Logo.abcad9b0257193e76d8d.png",
                marginTop: "10",
                marginRight: "0",
                marginBottom: "0",
                marginLeft: "0"
            };
        case "button":
            return {
                buttonCount: 1,
                buttonPlacement: "horizontal",
                buttons: [
                    {
                        text: "Button Text",
                        link: "",
                        fontSize: "16",
                        fontColor: "#000000",
                        backgroundColor: "#2563eb",
                        cornerRadius: "0",
                        alignment: "center",
                        bold: false,
                        italic: false,
                        underline: false,
                        addBorder: false,
                        borderColor: "#000000",
                        borderWeight: "1",
                        marginTop: "10",
                        marginRight: "0",
                        marginBottom: "0",
                        marginLeft: "0"
                    }
                ]
            };
        case "video":
            return {
                videoUploadLink: "https://www.youtube.com/embed/dQw4w9WgXcQ",
                videoLink: "https://www.youtube.com/embed/dQw4w9WgXcQ",
                marginTop: "10",
                marginRight: "0",
                marginBottom: "0",
                marginLeft: "0"
            };
        default:
            return {};
    }
};

const DraggableType = ({ type }) => {
    const { attributes, listeners, setNodeRef } = useDraggable({
        id: `palette-${type?.name}`,  // Use a unique ID for palette items
        data: { from: "palette", type: type?.name }
    });

    return (
        <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            p={2}
            borderWidth={1}
            borderRadius="md"
            mb={2}
            cursor="grab"
            display={"flex"}
            alignItems={"center"}
            style={{ backgroundColor: "white", border: "1px solid #CCCCCC", color: "#4A5568" }}
        >
            <span style={{ marginRight: ".8rem" }}>{type.icon}</span> {type?.name.charAt(0).toUpperCase() + type?.name.slice(1)}
        </Box>
    );
};

const DroppableCanvas = ({ children }) => {
    const { setNodeRef, isOver } = useDroppable({ id: "canvas" });
    return (
        <Box
            ref={setNodeRef}
            p={4}
            borderWidth={2}
            borderStyle="dashed"
            // minH="300px"
            bg={isOver ? "blue.50" : "transparent"}
            transition="background-color 0.2s"
        >
            {children}
        </Box>
    );
};

const SortableBlock = ({ id, block, onChange, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        data: { type: "block", block }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
  
    };

    return (
        <Box ref={setNodeRef} style={style} mb={4} borderWidth={1} borderRadius="md" p={3} bg="#F5F5F5">
            <Flex justifyContent="space-between" alignItems="center" style={{ borderBottom: "1px solid rgb(184, 181, 181)", paddingBottom: "5px" }}>
                <Text fontWeight="bold">
                    {block.type.charAt(0).toUpperCase() + block.type.slice(1)}
                </Text>
                <div style={{ display: "flex", alignItems: "center", gap: "5px", justifyContent: "center" }}>


                    {/* </Button> */}
                    <Box {...attributes} {...listeners} cursor="grab">

                        <LuMove fontSize={20} color={"gray.400"} />
                    </Box>
                    <Menu>
                        <MenuButton >
                            <LuMoreVertical />
                        </MenuButton>
                        <MenuList>
                            <MenuItem onClick={() => onDelete(block.id)}><Icon as={LuTrash2} /><span style={{ marginLeft: "5px" }}>Delete</span></MenuItem>

                        </MenuList>
                    </Menu>
                </div>

            </Flex>

            {block.type === "text" ? (
                // Use the separated TextBlockEditor component for text blocks
                <TextBlockEditor block={block} onChange={onChange} />
            ) : block.type === "button" && (
                <ButtonBlockEditor block={block} onChange={onChange} />
            )}

            {block.type === "image" && (
                <ImageBlockEditor block={block} onChange={onChange} />
            )}

            {block.type === "video" && (
                <VideoBlockEditor block={block} onChange={onChange} />
            )}


        </Box>
    );
};

const BlockPreview = ({ block }) => {
    switch (block.type) {
        case "text":
            return <TextBlockPreview block={block} />;
        case "button":
            return (
                <ButtonBlockPreview block={block} />
            );
        case "image":
            return <ImageBlockPreview block={block} />;
        case "video":
            return (
                <VideoBlockPreview block={block} />


            );
        default:
            return null;
    }
};

export default function MessageBuilder() {
    const [blocks, setBlocks] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [activeBlock, setActiveBlock] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8, // 8px of movement required before drag starts
        }
    }));

    const handleDragStart = (event) => {
        const { active } = event;
        setActiveId(active.id);

        // If we're dragging a block, store its data
        if (active.data.current?.type === "block") {
            setActiveBlock(active.data.current.block);
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
    
        // Check if we're dragging from palette
        if (active.id.toString().startsWith('palette-') && over && over.id === "canvas") {
            // Extract the block type from the palette ID
            const type = active.data.current.type;
    
            // Create a new block
            const newBlock = {
                id: Date.now().toString(),
                type: type,
                props: getDefaultProps(type)
            };
    
            setBlocks((prev) => [...prev, newBlock]);
        }
        // Check if we're dragging from palette but over a specific block
        else if (active.id.toString().startsWith('palette-') && over && over.id !== "canvas") {
            // Extract the block type from the palette ID
            const type = active.data.current.type;
    
            // Create a new block
            const newBlock = {
                id: Date.now().toString(),
                type: type,
                props: getDefaultProps(type)
            };
    
            // Find the index of the block we're hovering over
            const overIndex = blocks.findIndex(b => b.id.toString() === over.id.toString());
            
            // Insert the new block at the position of the block we're hovering over
            if (overIndex !== -1) {
                const newBlocks = [...blocks];
                newBlocks.splice(overIndex, 0, newBlock);
                setBlocks(newBlocks);
            } else {
                // Fallback - just append to the end
                setBlocks((prev) => [...prev, newBlock]);
            }
        }
        // Handling reordering blocks
        else if (over && !active.id.toString().startsWith('palette-')) {
            const oldIndex = blocks.findIndex(b => b.id.toString() === active.id.toString());
    
            // If over.id is "canvas", put it at the end
            if (over.id === "canvas") {
                if (oldIndex !== blocks.length - 1 && oldIndex !== -1) {
                    const newBlocks = [...blocks];
                    const [movedItem] = newBlocks.splice(oldIndex, 1);
                    newBlocks.push(movedItem);
                    setBlocks(newBlocks);
                }
            }
            // If over.id is a block, insert before/after that block
            else {
                const newIndex = blocks.findIndex(b => b.id.toString() === over.id.toString());
                if (oldIndex !== newIndex && oldIndex !== -1 && newIndex !== -1) {
                    setBlocks(blocks => arrayMove(blocks, oldIndex, newIndex));
                }
            }
        }
    
        // Reset active states
        setActiveId(null);
        setActiveBlock(null);
    };

    const handleDeleteBlock = (blockId) => {
        setBlocks((prev) => prev.filter((b) => b.id.toString() !== blockId.toString()));
    };

    // Convert blocks to sortable items
    const items = blocks.map(block => block.id.toString());
   
    return (
        <ChakraProvider theme={theme}>
            <Header />
            <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <Box display="flex" p={4} gap={6} width="100%">
                    {/* Left: Palette and Editor */}
                    <Box width="100%">
                        <Box display="flex" p={4} gap={6}>
                            {/* Palette */}
                            <Box width="20%"  backgroundColor={"#F5F5F5"} p={2} position="sticky" top="16%" height="fit-content">
                                <Text fontWeight="bold" mb={2}>Elements</Text>
                                <Divider mb={4} />
                                <Box mb={4} backgroundColor={"#F5F5F5"} borderRadius="md"  >
                                    {blockTypes.map((type, index) => (
                                        <DraggableType key={index} type={type} />
                                    ))}
                                </Box>
                            </Box>

                            {/* Editor Canvas */}
                            <Box width="80%">
                                <Text fontWeight="bold" mb={2}>Editor</Text>
                                <DroppableCanvas >
                                    <SortableContext items={items} strategy={verticalListSortingStrategy} >
                                        <VStack spacing={4} align="stretch">
                                            {blocks?.length > 0 ? (
                                                blocks.map((block) => (
                                                    <SortableBlock
                                                        key={block.id}
                                                        id={block.id.toString()}
                                                        block={block}
                                                        onChange={(updated) => {
                                                            setBlocks((prev) =>
                                                                prev.map((b) => (b.id === updated.id ? updated : b))
                                                            );
                                                        }}
                                                        onDelete={handleDeleteBlock}
                                                    />
                                                ))
                                            ) : (
                                                <Flex
                                                    height="100%"
                                                    minH={200}
                                                    justify="center"
                                                    align="center"
                                                    p={4}
                                                    borderRadius="md"
                                                >
                                                    <Text fontSize="lg" fontWeight="medium" color="gray.300" textAlign="center">
                                                        Please Drag & Drop Elements to Start Building Your Message
                                                    </Text>
                                                </Flex>
                                            )}
                                        </VStack>
                                    </SortableContext>
                                </DroppableCanvas>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right: Mobile Preview */}
                    <Box width="30%" p={4} position="sticky" top="15%" height="fit-content" display="flex" justifyContent="center" alignItems="center">
                        {/* <Text fontSize="xl" mb={4} fontWeight="bold">Mobile Preview</Text> */}
                        {/* <Divider mb={4} /> */}
                        <div style={{ position: "relative", width: "300px", height: "600px", backgroundImage: `url(${phoneImage})`, backgroundSize: "cover", backgroundPosition: "center" }}>
                            <div style={{
                                position: "absolute",
                                top: "80px",     // adjust based on your phone image screen area
                                left: "20px",    // adjust based on your phone image screen area
                                width: "260px",  // adjust based on your phone screen area
                                height: "480px",
                                overflow: "auto",
                                backgroundColor: "white", // optional, or make it transparent
                                // borderRadius: "16px"      // optional, for rounded corners
                            }}>
                                {blocks.length > 0 ? (

                                    blocks.map((block) => (

                                        <BlockPreview key={block.id} block={block} />


                                    ))
                                ) : (
                                    <Flex
                                        height="100%"
                                        minH={200}
                                        justify="center"
                                        align="center"
                                        p={4}
                                        borderRadius="md"
                                    >
                                        <Text fontSize="md" color="gray.400" textAlign="center">
                                            Your preview will appear here
                                        </Text>
                                    </Flex>
                                )}
                            </div></div>
                    </Box>
                </Box>

                {/* Drag Overlay */}
                <DragOverlay>
                    {activeId && activeId.toString().startsWith('palette-') ? (
                        <Box p={2} borderWidth={1} borderRadius="md" bg="gray.100">
                            {activeId.replace('palette-', '').charAt(0).toUpperCase() +
                                activeId.replace('palette-', '').slice(1)}
                        </Box>
                    ) : activeBlock ? (
                        <Box p={3} borderWidth={1} borderRadius="md" bg="white" width="300px">
                            <Text fontWeight="bold">
                                {activeBlock.type.charAt(0).toUpperCase() + activeBlock.type.slice(1)}
                            </Text>
                            {activeBlock.type === "text" && (
                                <Text mt={2}>{activeBlock.props.text}</Text>
                            )}
                            {activeBlock.type === "button" && (
                                <Button mt={2} size="sm">{activeBlock.props.label}</Button>
                            )}
                            {activeBlock.type === "image" && (
                                <Text mt={2}>Image: {activeBlock.props.alt}</Text>
                            )}
                            {activeBlock.type === "video" && (
                                <Text mt={2}>Video</Text>
                            )}
                        </Box>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </ChakraProvider>
    );
}